import { ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { DataTableColumn, DataTableComponent, DataTablePageChangedEvent } from '@shared/components/data-table.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { InterventionStore } from '@features/intervention/intervention.store';
import { INTERVENTION_STAGES, stageLabel } from '@features/intervention/intervention-stage';
import { EmployeeInterventionsStore } from '../../employee-interventions.store';
import { EmployeeInterventionResponse } from '../../employee.model';
import { toDateOnlyString } from '@shared/utils/date-utils';

// The Interventions tab of the employee page: every intervention the employee owns or their dependants own, paged and
// filterable on the server. A caller who may not see the clinical detail (the client portal) is not sent it, so those
// columns are left out here too.
@Component({
  selector: 'employee-interventions-panel',
  imports: [RouterLink, DataTableComponent, ...SharedMaterialUIModule],
  templateUrl: './employee-interventions-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmployeeInterventionsPanelComponent {
  protected readonly store = inject(EmployeeInterventionsStore);
  protected readonly interventionStore = inject(InterventionStore);

  readonly employeeId = input.required<string>();
  // True where the panel sits inside a form (the counselling intake): View then opens the intervention in a new tab so the form is not left.
  readonly openInNewTab = input(false);

  protected readonly stages = INTERVENTION_STAGES;
  protected readonly stageLabel = stageLabel;
  protected readonly today = new Date();

  private readonly clinicalDetailVisible = computed(() => this.store.items()[0]?.clinicalDetailVisible ?? true);

  protected readonly columns = computed<DataTableColumn<EmployeeInterventionResponse>[]>(() => {
    const plain = (cellClass = 'px-3 py-3') => cellClass;

    const leading: DataTableColumn<EmployeeInterventionResponse>[] = [
      {
        id: 'interventionNumber',
        header: 'Case number',
        value: (item) => item.interventionNumber,
        cellClass: 'px-3 py-3 font-medium',
      },
      { id: 'createdDate', header: 'Created date', value: (item) => item.createdDate.slice(0, 10), cellClass: plain() },
      {
        id: 'owner',
        header: 'Owner',
        value: (item) => (item.ownedByDependant ? `${item.ownerName} (dependant)` : item.ownerName),
        cellClass: plain(),
      },
    ];

    const clinical: DataTableColumn<EmployeeInterventionResponse>[] = [
      { id: 'problemCluster', header: 'Problem cluster', value: (item) => item.problemCluster, cellClass: plain() },
      { id: 'primaryProblem', header: 'Primary problem', value: (item) => item.primaryProblem, cellClass: plain() },
      { id: 'outcomeAfterIntake', header: 'Outcome after intake', value: (item) => item.outcomeAfterIntake, cellClass: plain() },
      { id: 'caseOutcome', header: 'Case outcome', value: (item) => item.caseOutcome, cellClass: plain() },
    ];

    const trailing: DataTableColumn<EmployeeInterventionResponse>[] = [
      { id: 'numberOfSessions', header: 'No of sessions', value: (item) => item.numberOfSessions, cellClass: plain() },
      { id: 'status', header: 'Status', value: (item) => stageLabel(item.status), cellClass: plain() },
    ];

    return [...leading, ...(this.clinicalDetailVisible() ? clinical : []), ...trailing];
  });

  constructor() {
    this.interventionStore.loadInterventionTypes();

    effect(() => this.store.open(this.employeeId()));
  }

  protected search(term: string): void {
    this.store.setFilter({ search: term });
  }

  protected pageChanged(event: DataTablePageChangedEvent): void {
    this.store.setPage(event.pageNumber, event.pageSize);
  }

  protected hasFilter(): boolean {
    const filter = this.store.filter();
    return Object.values(filter).some(Boolean);
  }

  protected createdFrom(): Date | null {
    return parseDate(this.store.filter().createdFrom);
  }

  protected createdTo(): Date | null {
    return parseDate(this.store.filter().createdTo);
  }

  protected setCreatedFrom(date: Date | null): void {
    this.store.setFilter({ createdFrom: date ? toDateOnlyString(date) : undefined });
  }

  protected setCreatedTo(date: Date | null): void {
    this.store.setFilter({ createdTo: date ? toDateOnlyString(date) : undefined });
  }
}


function parseDate(value: string | undefined): Date | null {
  return value ? new Date(`${value}T00:00:00`) : null;
}
